/**
 * Shopify integration helpers — used by the per-brand Shopify card in the
 * Integrations grid (`pages/integrations/MCP/MCP.tsx`).
 *
 * The Shopify reporting MCP is credential-based (like Semrush): the merchant
 * pastes their store domain + a custom-app Admin API access token. These
 * helpers normalize/validate both fields before they are submitted through
 * the standard MCP credentials endpoints.
 *
 * Contract with the backend (`shopify` TaraMCP catalog row): the backend
 * accepts EITHER credential shape (legacy UPPERCASE keys; Shopify deprecated
 * admin-created custom apps on 2026-01-01 — Dev Dashboard apps issue no
 * static token, so their client credentials mint a 24h Admin token instead):
 *   - `MYSHOPIFY_DOMAIN`         — always submitted normalized as
 *     `example.myshopify.com`.
 *   - token mode: `SHOPIFY_ACCESS_TOKEN` — write-only secret (`shpat_…`).
 *     Never displayed back; omitted from update payloads when left blank so
 *     the stored token is kept.
 *   - client-credentials mode: `SHOPIFY_CLIENT_ID` (visible) +
 *     `SHOPIFY_CLIENT_SECRET` (write-only secret, same omit-when-blank
 *     contract as the access token).
 * The two shapes are mutually exclusive — the submit handler strips the
 * inactive mode's fields from the payload.
 */

import { CENTRAL_API_BASE_URL } from '../constants/central-api';

export const SHOPIFY_MCP_SLUG = 'shopify';

/**
 * The public OAuth callback the AGENCY must register on their own Shopify app
 * (bring-your-own-app mode). Environment-correct via CENTRAL_API_BASE_URL —
 * note that constant carries a TRAILING slash, hence the trim.
 */
export const SHOPIFY_AGENCY_APP_CALLBACK_URL = `${CENTRAL_API_BASE_URL.replace(
  /\/+$/,
  ''
)}/integrations/oauth/shopify/callback`;

/**
 * The origin (scheme + host) of the callback above. The agency must set their
 * Shopify app's **App URL** to this same host: Shopify's authorize endpoint
 * rejects the install with `invalid_request: The redirect_uri and application
 * url must have matching hosts` when the App URL host differs from the redirect
 * host. Registering the redirect URL alone is NOT enough.
 */
export const SHOPIFY_AGENCY_APP_URL_ORIGIN = new URL(
  SHOPIFY_AGENCY_APP_CALLBACK_URL
).origin;
export const SHOPIFY_DOMAIN_FIELD = 'MYSHOPIFY_DOMAIN';
export const SHOPIFY_TOKEN_FIELD = 'SHOPIFY_ACCESS_TOKEN';
export const SHOPIFY_CLIENT_ID_FIELD = 'SHOPIFY_CLIENT_ID';
export const SHOPIFY_CLIENT_SECRET_FIELD = 'SHOPIFY_CLIENT_SECRET';

/**
 * How a Shopify store is connected.
 *
 * - `oauth` — connect via ADUP's own Shopify app. The merchant enters their
 *   store domain and is redirected to Shopify to approve the install; the
 *   backend exchanges the code for a permanent offline token. Shopify has not
 *   approved that app yet, so it only works for stores Shopify lets install it
 *   (the app reviewer's among them). Still offered, labelled as awaiting
 *   approval.
 * - `agency_app` — the DEFAULT, bring-your-own-app: the agency created its OWN
 *   custom-distribution Shopify app for this specific store (one app per
 *   store) and registered ADUP's public callback as that app's redirect URL.
 *   The agency enters the app's Client ID + Secret here; the backend stores
 *   them on the brand and OAuths against THEIR app. This works TODAY for any
 *   store — no App Store review involved.
 * - `token` — a pasted `shpat_` Admin API token. Retained ONLY so existing
 *   connections keep working: Shopify retired merchant-admin custom apps, so
 *   new stores cannot obtain one of these.
 * - `client_credentials` — ☠️ DEAD. Shopify refuses this grant on any store
 *   outside our own org: `400 shop_not_permitted — "Client credentials cannot
 *   be performed on this shop"` (verified live on menyard.myshopify.com,
 *   2026-07-17). It can never work for a client store. Kept only to render
 *   already-saved connections; never offer it for a new one. (The same
 *   id+secret fields now power `agency_app`, which uses the auth-code grant
 *   those apps DO support.)
 */
export type ShopifyAuthMode = 'oauth' | 'agency_app' | 'token' | 'client_credentials';

/**
 * The mode a NEW Shopify connection should use: the agency's own app, the one
 * mode that works for every store until Shopify approves ADUP's app.
 */
export const SHOPIFY_DEFAULT_AUTH_MODE: ShopifyAuthMode = 'agency_app';

/**
 * The mode the connect popup opens in. A new connection starts at the
 * default; an existing one opens in `agency_app` when it has a stored client
 * id (the pair the brand-app flow reconnects with) and in `oauth` otherwise,
 * because a store installed through ADUP's app or a pasted token has no
 * client pair to show.
 */
export const getShopifyInitialAuthMode = (
  isExistingConnection: boolean,
  credentials: any
): ShopifyAuthMode => {
  if (!isExistingConnection) return SHOPIFY_DEFAULT_AUTH_MODE;
  const savedClientId = credentials?.[SHOPIFY_CLIENT_ID_FIELD];
  return typeof savedClientId === 'string' && savedClientId.trim() !== ''
    ? 'agency_app'
    : 'oauth';
};

/** The connect options in the order the popup offers them, as label keys. */
export const SHOPIFY_AUTH_MODE_OPTIONS: [ShopifyAuthMode, string][] = [
  ['agency_app', 'Shopify-AuthModeAgencyApp'],
  ['oauth', 'Shopify-AuthModeOAuthAwaitingApproval'],
  ['token', 'Shopify-AuthModeToken'],
];

/**
 * Fields rendered in the connection form for each auth mode.
 *
 * `oauth` collects only the store domain — the token comes back from Shopify,
 * so there is no secret for the merchant to paste at all.
 */
export const getShopifyCredentialFields = (mode: ShopifyAuthMode): string[] => {
  if (mode === 'oauth') return [SHOPIFY_DOMAIN_FIELD];
  if (mode === 'agency_app' || mode === 'client_credentials') {
    return [SHOPIFY_DOMAIN_FIELD, SHOPIFY_CLIENT_ID_FIELD, SHOPIFY_CLIENT_SECRET_FIELD];
  }
  return [SHOPIFY_DOMAIN_FIELD, SHOPIFY_TOKEN_FIELD];
};

/**
 * Infer the auth mode of an EXISTING connection from its saved credentials.
 *
 * Secrets are stripped from what the backend returns, so this reads the
 * non-secret markers: a stored client id ⇒ the (dead) client-credentials shape.
 * An OAuth-installed store is indistinguishable from a pasted-token one here —
 * both hold `SHOPIFY_ACCESS_TOKEN` — and that is fine: they behave identically
 * from this point on, since OAuth's only job was to produce that token.
 *
 * Used for RENDERING SAVED state only. A NEW connection must start at
 * {@link SHOPIFY_DEFAULT_AUTH_MODE}.
 */
export const getShopifyAuthMode = (credentials: any): ShopifyAuthMode =>
  credentials && typeof credentials[SHOPIFY_CLIENT_ID_FIELD] === 'string' &&
  credentials[SHOPIFY_CLIENT_ID_FIELD].trim() !== ''
    ? 'client_credentials'
    : 'token';

/** Valid, fully-normalized Shopify store domain. */
export const SHOPIFY_DOMAIN_PATTERN = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/;

/**
 * Normalize any reasonable way a merchant pastes their store into the
 * canonical `example.myshopify.com` form. Accepts:
 *   - a bare store handle: `acme`
 *   - the domain itself: `acme.myshopify.com` (any casing)
 *   - a pasted URL: `https://acme.myshopify.com/admin`, `www.acme.myshopify.com`
 *   - a Shopify admin URL: `https://admin.shopify.com/store/acme`
 * Returns the input trimmed/lowercased with protocol/path stripped; a bare
 * handle gets `.myshopify.com` appended. Does NOT guarantee validity — run the
 * result through `isValidShopifyDomain`.
 */
export const normalizeShopifyDomain = (input: string): string => {
  if (typeof input !== 'string') return '';
  let value = input.trim().toLowerCase();
  if (value === '') return '';

  // Strip protocol (https://, http:// …)
  value = value.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');

  // Modern admin URL: admin.shopify.com/store/<handle>
  const adminMatch = value.match(/^admin\.shopify\.com\/store\/([^/?#]+)/);
  if (adminMatch) {
    value = adminMatch[1];
  }

  // Strip path / query / hash, then port, then a leading www.
  value = value.split(/[/?#]/)[0];
  value = value.split(':')[0];
  value = value.replace(/^www\./, '');

  if (value === '') return '';

  // Bare store handle → canonical myshopify domain.
  if (!value.includes('.')) {
    return `${value}.myshopify.com`;
  }

  return value;
};

/** True when `domain` is an already-normalized, valid myshopify.com domain. */
export const isValidShopifyDomain = (domain: string): boolean =>
  typeof domain === 'string' && SHOPIFY_DOMAIN_PATTERN.test(domain);

/**
 * True when `token` looks like a Shopify Admin API access token (custom-app
 * tokens always start with `shpat_`).
 */
export const isValidShopifyToken = (token: string): boolean =>
  typeof token === 'string' && /^shpat_\S+$/.test(token.trim());

/**
 * True when `clientId` looks like a Shopify Dev Dashboard app Client ID.
 * Deliberately loose (no whitespace, minimum length) — Shopify documents no
 * stable format for it; the backend validates with the same looseness.
 */
export const isValidShopifyClientId = (clientId: string): boolean =>
  typeof clientId === 'string' && /^[A-Za-z0-9_-]{12,}$/.test(clientId.trim());

/** True when `secret` looks like a Shopify Dev Dashboard app Client Secret. */
export const isValidShopifyClientSecret = (secret: string): boolean =>
  typeof secret === 'string' && /^\S{16,}$/.test(secret.trim());
